import { useMemo, useRef, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { Identifier, XYCoord } from "dnd-core";
import { SelectChangeEvent } from "@mui/material";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
	changeRobotEventActionData,
	changeRobotExceptionActionData,
	deleteActionRobot,
	deleteRobotException,
	replaceActionExceptionRobot,
	replaceActionRobot,
	selectActiveRobotVersion,
} from "@/store/ses/sesRobotSlice";
import {
	changeDataElemEventActionData,
	changeEndpointActionData,
	changeEndpointEntryActionData,
	changeExceptionActionData,
	deleteActionDataElem,
	deleteActionEndpoint,
	deleteActionEndpointEntry,
	deleteException,
	replaceActionDataElem,
	replaceActionEndpoint,
	replaceActionEndpointEntry,
	replaceActionException,
} from "@/store/ses/sesItemConfigSlice";
import { selectUploadFile } from "@/store/ses/sesSlice";
import { useAccessRight, useTranslate } from "@/hooks";
import { SES } from "@/constants/accessRights";
import {
	ACTION_BREAK,
	ACTION_CONFIRMATION,
	ACTION_DECLINE_DATA,
	ACTION_END,
	ACTION_EXTERNAL,
	ACTION_GO_TO,
	ACTION_QAS_ASK,
	ACTION_QAS_CHAT,
	ACTION_SAY,
	ACTION_SEND_FILE,
	ACTION_SET_REPORT,
	ACTION_SET_VARIABLE,
	ACTION_TRANSFER,
} from "@/constants/defaultValues";
import { ActionVariantType, ConditionVariantType } from "@/types/ses/sesItemConfigTypes";
import { DragItem, IActionProps, IHandleSaveActionProps, TExtendedAction } from "./ActionEvent.props";
import { selectSesHints } from "@/store/hintsSlice";

export const useActionEvent = ({ action, idx, channel, changeFlgHandler, actionFor, moveAction }: IActionProps) => {
	const [selectExtendedAction, setSelectExtendedAction] = useState<TExtendedAction>(() => {
		if (action.action === "say") {
			if (action.type === "internal") {
				return action.service;
			} else if (action.type === "external") {
				return "external";
			} else {
				return action.action;
			}
		}
		return action.action;
	}); // расширенное действие

	const [showPhraseList, setShowPhraseList] = useState(false); // показ полного списка фраз
	const [numberOfNonExistentCategories, setNumberOfNonExistentCategories] = useState<number>(0); // кол-во несуществующих категорий

	const fieldSetRef = useRef<HTMLDivElement>(null); // ссылка на контейнер
	const [anchorElConditionsRun, setAnchorElConditionsRun] = useState<HTMLElement | null>(null); // якорь для условий запуска действий
	const [anchorElCategoriesSettings, setAnchorElCategoriesSettings] = useState<HTMLElement | null>(null); // якорь для условий присвоения категорий

	const [showCopyAndMoveForm, setShowCopyAndMoveForm] = useState<{ isShown: boolean; type: "copy" | "move" }>({ isShown: false, type: "copy" }); // показ формы копирования/перемещения действия
	const [showAlertDialogDelAction, setShowAlertDialogDelAction] = useState<boolean>(false); // показ диалогового окна при удалении действия

	const dispatch = useAppDispatch();
	const activeRobotVersion = useAppSelector(selectActiveRobotVersion); // store - версия активного робота
	const uploadFileStatus = useAppSelector(selectUploadFile); // store - статус загрузки файла
	const sesHints = useAppSelector(selectSesHints);

	const isAccess = useAccessRight(); // hook для проверки прав доступа
	const translate = useTranslate(); // hook для перевода текста

	const isDisabled = useMemo(() => {
		return (
			((actionFor.for === "robotEvent" || actionFor.for === "robotException") && !isAccess(SES.ROBOT_EDIT)) ||
			((actionFor.for === "dataElement" || actionFor.for === "dataException") && !isAccess(SES.DATA_EDIT)) ||
			((actionFor.for === "endpoint" || actionFor.for === "endpointEntry" || actionFor.for === "endpointException") && !isAccess(SES.ENDPOINT_EDIT)) ||
			activeRobotVersion !== "draft"
		);
	}, []); // отключен

	// обработчик изменения расширенного действия
	const handleExtendedActionsChange = (e: SelectChangeEvent<TExtendedAction>): void => {
		const value = e.target.value as TExtendedAction;
		setSelectExtendedAction(value);
		const data: Partial<{ actionId: string; conditions: ConditionVariantType[][] }> = { actionId: action.actionId, conditions: action.conditions };
		if (value === "say") handleReplaceAction({ ...ACTION_SAY, ...data });
		else if (value === "confirmation") handleReplaceAction({ ...ACTION_CONFIRMATION, ...data });
		else if (value === "sendfile") handleReplaceAction({ ...ACTION_SEND_FILE, ...data });
		else if (value === "setvariable") handleReplaceAction({ ...ACTION_SET_VARIABLE, ...data });
		else if (value === "goto") handleReplaceAction({ ...ACTION_GO_TO, ...data });
		else if (value === "setreport") handleReplaceAction({ ...ACTION_SET_REPORT, ...data });
		else if (value === "declinedata") handleReplaceAction({ ...ACTION_DECLINE_DATA, ...data });
		else if (value === "transfer") handleReplaceAction({ ...ACTION_TRANSFER, ...data });
		else if (value === "break") handleReplaceAction({ ...ACTION_BREAK, ...data });
		else if (value === "end") handleReplaceAction({ ...ACTION_END, ...data });
		else if (value === "external") handleReplaceAction({ ...ACTION_EXTERNAL, ...data });
		else if (value === "qas-ask") handleReplaceAction({ ...ACTION_QAS_ASK, ...data });
		else if (value === "qas-chat") handleReplaceAction({ ...ACTION_QAS_CHAT, ...data });
	};

	// обработчик замены действия
	const handleReplaceAction = (actionObj: ActionVariantType): void => {
		const data: { channel: string; idx: number; action: ActionVariantType } = { channel, idx, action: actionObj };
		if (actionFor.for === "robotException") {
			dispatch(replaceActionExceptionRobot({ exception: actionFor.exception, ...data })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(replaceActionException({ targetObject: "dataElement", exception: actionFor.exception, ...data })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(replaceActionException({ targetObject: "endpoint", exception: actionFor.exception, ...data })); // исключения КТ
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(replaceActionEndpointEntry({ ...data })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(replaceActionRobot({ actionEvent: actionFor.event, ...data })); // робота
			actionFor.for === "dataElement" && dispatch(replaceActionDataElem({ actionEvent: actionFor.event, ...data })); // ЭД
			actionFor.for === "endpoint" && dispatch(replaceActionEndpoint({ ...data })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	// обработчик сохранения действия в store
	const handleSaveActionInStore = (data: IHandleSaveActionProps): void => {
		if (actionFor.for === "robotException") {
			dispatch(changeRobotExceptionActionData({ exception: actionFor.exception, ...data })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(changeExceptionActionData({ targetObject: "dataElement", exception: actionFor.exception, ...data })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(changeExceptionActionData({ targetObject: "endpoint", exception: actionFor.exception, ...data })); // исключения КТ
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(changeEndpointEntryActionData({ ...data })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(changeRobotEventActionData({ actionEvent: actionFor.event, ...data })); // робота
			actionFor.for === "dataElement" && dispatch(changeDataElemEventActionData({ actionEvent: actionFor.event, ...data })); // ЭД
			actionFor.for === "endpoint" && dispatch(changeEndpointActionData({ ...data })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	// обработчик удаления действия
	const deleteActionHandler = (): void => {
		const data = { channel, idx };
		if (actionFor.for === "robotException") {
			dispatch(deleteRobotException({ exception: actionFor.exception, ...data })); // исключения робота
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "dataException") {
			dispatch(deleteException({ targetObject: "dataElement", exception: actionFor.exception, ...data })); // исключения ЭД
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointException") {
			dispatch(deleteException({ targetObject: "endpoint", exception: actionFor.exception, ...data })); // исключения КТ
			changeFlgHandler("exceptions"); // ставим флаг о несохраненных данных
		} else if (actionFor.for === "endpointEntry") {
			dispatch(deleteActionEndpointEntry({ ...data })); // входа КТ
			changeFlgHandler("entry"); // ставим флаг о несохраненных данных
		} else {
			actionFor.for === "robotEvent" && dispatch(deleteActionRobot({ actionEvent: actionFor.event, ...data })); // события робота
			actionFor.for === "dataElement" && dispatch(deleteActionDataElem({ actionEvent: actionFor.event, ...data })); // ЭД
			actionFor.for === "endpoint" && dispatch(deleteActionEndpoint({ ...data })); // КТ
			changeFlgHandler("actions"); // ставим флаг о несохраненных данных
		}
	};

	const [{ isDragging }, drag] = useDrag({
		type: "action",
		item: () => {
			return { id: idx, index: idx };
		},
		collect: (monitor: any) => ({
			isDragging: monitor.isDragging(),
		}),
	});

	const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
		accept: "action",
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			};
		},
		hover(item: DragItem, monitor) {
			if (!fieldSetRef.current) {
				return;
			}

			// Используйте requestAnimationFrame для тяжелых операций
			requestAnimationFrame(() => {
				const dragIndex = item.index;
				const hoverIndex = idx;

				if (dragIndex === hoverIndex) return;

				const hoverBoundingRect = fieldSetRef.current?.getBoundingClientRect();
				if (!hoverBoundingRect) return;

				const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
				const clientOffset = monitor.getClientOffset();

				if (!clientOffset) return;

				const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top;

				if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
				if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

				// Важно: используйте debounce или throttle для moveAction
				moveAction(dragIndex, hoverIndex);
				item.index = hoverIndex;
			});
		},
	});

	return {
		// Состояния
		selectExtendedAction,
		numberOfNonExistentCategories,
		fieldSetRef,
		anchorElConditionsRun,
		anchorElCategoriesSettings,
		showCopyAndMoveForm,
		showAlertDialogDelAction,
		showPhraseList,
		uploadFileStatus,
		isDisabled,
		isDragging,
		handlerId,
		sesHints,

		// Сеттеры состояний
		setAnchorElConditionsRun,
		setAnchorElCategoriesSettings,
		setShowCopyAndMoveForm,
		setShowAlertDialogDelAction,
		setShowPhraseList,
		setNumberOfNonExistentCategories,

		// Обработчики
		handleExtendedActionsChange,
		handleSaveActionInStore,
		deleteActionHandler,
		drag,
		drop,

		//хелперы
		translate,
	};
};
